import {UserTracks, UserTracksList} from "@share/web-conferences/types/UserTracks";
import Vue from "vue";
import {
    ILocalAudioTrack, ILocalTrack,
    ILocalVideoTrack,
    UID
} from "agora-rtc-sdk-ng";
import {TracksManagerInterface} from "@share/web-conferences/interfaces/TracksManagerInterface";
import {AgoraMediaType} from "@share/web-conferences/types/AgoraMediaType";
import {AgoraMediaType as AgoraMediaTypeEnum} from "@share/web-conferences/enums/AgoraMediaType";
import {TracksStatuses} from "@share/web-conferences/types/TracksStatuses";
import {AgoraInterface} from "@share/web-conferences/interfaces/AgoraInterface";

export class TracksManager implements TracksManagerInterface {
    tracks: UserTracksList = Vue.observable({});
    localUId: UID;
    audioStates: Record<UID, boolean> = Vue.observable({});
    videoStates: Record<UID, boolean> = Vue.observable({});
    isPlaying = true;
    isAutoPlayError = false;

    async add(tracks: UserTracks, tracksStatuses: TracksStatuses | null = null): Promise<void> {
        if (tracks.isLocal && !tracks.isScreenShare) {
            this.localUId = tracks.uid;
        }

        Vue.set(this.tracks, tracks.uid, Vue.observable(tracks));

        this.changeAudioState(tracks.uid, tracksStatuses != null ? tracksStatuses.audio : Boolean(tracks.audio));
        this.changeVideoState(tracks.uid, tracksStatuses != null ? tracksStatuses.video : Boolean(tracks.video));
    }

    async update(tracks: UserTracks, mediaType: AgoraMediaType | null = null): Promise<void> {
        if (!this.tracks[tracks.uid]) {
            await this.add(tracks);
        } else {
            if (mediaType == null || mediaType == AgoraMediaTypeEnum.Audio) {
                Vue.set(this.tracks[tracks.uid], 'audio', tracks.audio);
                this.changeAudioState(tracks.uid, Boolean(tracks.audio));
            }
            if (mediaType == null || mediaType == AgoraMediaTypeEnum.Video) {
                Vue.set(this.tracks[tracks.uid], 'video', tracks.video);
                this.changeVideoState(tracks.uid, Boolean(tracks.video));
            }
        }
    }

    async updateLocal(track: ILocalTrack, mediaType: AgoraMediaType): Promise<void> {
        let data = {
            uid: this.localUId,
            isLocal: true
        } as UserTracks;

        if (mediaType == "video") {
            data.video = track as ILocalVideoTrack;
        }
        if (mediaType == "audio") {
            data.audio = track as ILocalAudioTrack;
        }

        await this.update(data, mediaType);
    }

    async remove(uid: UID, doRelease = false): Promise<void> {
        if (doRelease && this.tracks[uid]) {
            this.releaseTrack(this.tracks[uid]);
        }
        Vue.delete(this.tracks, uid);
        Vue.delete(this.audioStates, uid);
        Vue.delete(this.videoStates, uid);
    }

    async removeLocal(): Promise<void> {
        if (this.localUId && this.tracks[this.localUId]) {
            this.releaseTrack(this.tracks[this.localUId]);
            await this.remove(this.localUId);
        }
    }

    releaseTrack(userTracks: UserTracks) {
        userTracks.video?.stop();
        userTracks.audio?.stop();
        userTracks.video?.removeAllListeners();
        userTracks.audio?.removeAllListeners();
        if (userTracks.isLocal || userTracks.isScreenShare) {
            (userTracks.video as ILocalVideoTrack)?.close?.();
            (userTracks.audio as ILocalAudioTrack)?.close?.();
        }
    }

    async release(): Promise<void> {
        Object.values(this.tracks).forEach(item => this.releaseTrack(item));
        this.tracks = {};
        this.audioStates = {};
        this.videoStates = {};
    }

    async switchLocalMic(agora: AgoraInterface, state: boolean | null = null): Promise<void> {
        let newEnabledValue = state == null ? !this.audioStates[this.localUId] : state;
        this.changeAudioState(this.localUId, newEnabledValue);

        if (this.tracks[this.localUId].audio) {
            await (this.tracks[this.localUId].audio as ILocalAudioTrack).setEnabled(newEnabledValue);
        }
        else if (state) {
            Vue.set(this.tracks[this.localUId], 'audio', await agora.createAudioTrack() as ILocalAudioTrack);
            await agora.publishLocalTracks([this.tracks[this.localUId].audio as ILocalTrack]);
        }
    }

    async switchLocalVideo(agora: AgoraInterface, state: boolean | null = null): Promise<void> {
        let newEnabledValue = state == null ? !this.videoStates[this.localUId] : state;
        this.changeVideoState(this.localUId, newEnabledValue);

        if (this.tracks[this.localUId].video) {
            (this.tracks[this.localUId].video as ILocalVideoTrack).setEnabled(newEnabledValue);
        }
        else if (state) {
            Vue.set(this.tracks[this.localUId], 'video', await agora.createVideoTrack() as ILocalVideoTrack);
            await agora.publishLocalTracks([this.tracks[this.localUId].video as ILocalTrack]);
        }
    }

    changeAudioState(uid: UID, value: boolean) {
        Vue.set(this.audioStates, uid, value);
        if (value && this.isPlaying && uid != this.localUId) {
            this.tracks[uid]?.audio?.play();

            //for detecting autoplay error
            (this.tracks[uid]?.audio as any)?._source?.audioElement?.play()
            .catch((e: any) => {
                if (e.name === "NotAllowedError") {
                    this.isAutoPlayError = true;
                }
            })
        }
    }

    changeVideoState(uid: UID, value: boolean) {
        Vue.set(this.videoStates, uid, value);
    }

    getLocalUserTracks(): UserTracks {
        return this.tracks[this.localUId] ?? {};
    }

    muteAll() {
        Object.values(this.tracks).forEach(item => {
           !item.isLocal && item.audio?.stop();
        });
        Vue.set(this, 'isPlaying', false);
    }

    unmuteAll() {
        Object.values(this.tracks).forEach(item => {
            !item.isLocal && item.audio?.play();
            //the line below is needed for resume AudioContext after click (user interaction) on mobile (on desktop it's not need)
            !item.isLocal && (item.audio as any)?._source?.audioElement?.play();
        });
        Vue.set(this, 'isPlaying', true);
    }

    isLocalScreenSharing(uid: UID): boolean {
        return Boolean(this.tracks && this.tracks[uid] && this.tracks[uid].isLocal && this.tracks[uid].isScreenShare);
    }

    clean(): void {
        this.tracks = {} as any;
        this.audioStates = {} as any;
        this.videoStates = {} as any;
    }
}
